import { useEffect, useState, useRef } from "react"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import moment from "moment"
import CheckBox from "aldoo-ra/Checkbox"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import LocalizationItemDialog from "./localization-item-dialog"
import LocalizationController from "./localization-controller"
import LocalizationKeyDialog from "./localization-key-dialog"
import LocalizationRebuildDialog from "./localization-rebuild-dialog"
import { usePromise } from "aldoo-ra/usePromise"
import { ListLocalizationKeys } from "./api"
import { FunnelIcon, PlusCircleIcon } from "@heroicons/react/24/outline"
import PubSub from "pubsub-js"

export default function LocalizationKeys() {
  const localizationController = LocalizationController()

  const [tableRefresh, setTableRefresh] = useState(false)
  const [languages, setLanguages] = useState([])

  const [searchInLanguage, setSearchInLanguage] = useState([])
  const [selectedType, setSelectedType] = useState(["Any"])
  const typeOptions = [
    { value: "Any", label: "Any Type" },
    { value: "text", label: "Text" },
    { value: "image", label: "Image" },
    { value: "audio", label: "Audio" },
    { value: "video", label: "Video" },
    { value: "object", label: "Object" },
  ]

  const [selectedLocalizationItem, setSelectedLocalizationItem] = useState(null)
  const [openRebuildDialog, setOpenRebuildDialog] = useState(false)
  const [openAddKeyDialog, setOpenAddKeyDialog] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const filterRef = useRef(null)
 
  const keyRenders = [
    {
      id: "key",
      title: `Key`,
      headerClassName: "w-[450px]",

      render: (value, item) => {
        return (
          <button
            className="block w-full truncate text-admin_text text-left hover:underline hover:text-admin_primary"
            onClick={async () => {
              //get a fresh copy of the item
              const result =
                await localizationController.getLocalizationKeyPreFormat(
                  item.key
                )

              setSelectedLocalizationItem(result)
            }}
          >
            {value}
          </button>
        )
      },
    },
    {
      id: "type",
      title: `Type`,
      headerClassName: "w-[100px]",
      render: (value) => {
        const getTypeStyles = (type) => {
          switch (type?.toLowerCase()) {
            case 'text':
              return "bg-admin_accent_3 text-white" // Green
            case 'object':
              return "bg-admin_accent_2 text-white" // Blue
            case 'image':
              return "bg-admin_accent_1 text-white" // Purple
            case 'video':
              return "bg-admin_accent_6 text-white" // Red
            case 'audio':
              return "bg-admin_accent_5 text-white" // Orange
            case 'file':
              return "bg-admin_accent_4 text-white" // Yellow
            default:
              return "bg-admin_neutral_3 text-white" // Grey
          }
        }

        return (
          <div className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${getTypeStyles(value)}`}>
            <span className="uppercase tracking-wide">{value || 'unknown'}</span>
          </div>
        )
      },
    },
    {
      id: "createdAt",
      title: `Created`,
      headerClassName: "p-2 w-[140px]",
      itemClassName: "p-2",
      render: (value) => {
        return (
          <div className="flex flex-col">
            <span className="text-admin_text">
              {moment(value).format("DD/MM/YYYY HH:mm")}
            </span>
            <span className="text-xs text-admin_text_secondary">
              {moment(value).fromNow()}
            </span>
          </div>
        )
      },
    },
    {
      id: "updatedAt",
      title: `Updated`,
      headerClassName: "p-2 w-[140px]",
      itemClassName: "p-2",
      render: (value) => {
        return (
          <div className="flex flex-col">
            <span className="text-admin_text">
              {moment(value).format("DD/MM/YYYY HH:mm")}
            </span>
            <span className="text-xs text-admin_text_secondary">
              {moment(value).fromNow()}
            </span>
          </div>
        )
      },
    },
  ]
  const [searchField, setSearchField] = useState("key")
  const [pending, setPending] = useState(false)

  const { result: languageContent, execute: refreshStatistics } = usePromise(
    localizationController.getLanguageStatistics
  )

  // Create a memoized search object that will be recreated when any of its dependencies change
  const [searchObject, setSearchObject] = useState({
    key: "",
    text: "",
    pending: false,
    in: [],
  })

  // Update search object when pending, searchInLanguage, or selectedType changes
  useEffect(() => {
    const newSearchObject = { pending, in: searchInLanguage }
    // Only include search fields if they have actual values, not empty strings
    setSearchObject(newSearchObject)
    setTableRefresh((prev) => !prev)
  }, [pending, searchInLanguage, selectedType])

  useEffect(() => {
    if (!languageContent) return
    localizationController.onRefreshStatistics(() => {
      refreshStatistics()
      setTableRefresh(!tableRefresh)
    })
  }, [languageContent])

  useEffect(() => {
    if (!languageContent) return
    setLanguages(languageContent?.languages)

    //find the primary language and set it to the controller
    const primaryLanguage = languageContent?.languages?.find(
      (language) => language.primary
    )
    localizationController.setPrimaryLanguage(primaryLanguage?.code)
    localizationController.setLanguages(languageContent?.languages)
  }, [languageContent])

  // Subscribe to localization data updates (e.g., after auto-translation)
  useEffect(() => {
    // Handler function for refreshing selected item
    const refreshSelectedItem = async () => {
      // If a localization item is currently open in the dialog, refresh it
      if (selectedLocalizationItem) {
        try {
          const refreshedItem = await localizationController.getLocalizationKeyPreFormat(
            selectedLocalizationItem.key
          )

          if (refreshedItem && refreshedItem.translations) {
            setSelectedLocalizationItem(refreshedItem)
          }
        } catch (error) {
          console.error('Failed to refresh selected localization item:', error)
        }
      }

      // Also refresh the table
      setTableRefresh(prev => !prev)
    }

    // Subscribe to both events
    const token1 = PubSub.subscribe('LOCALIZATION_DATA_UPDATED', refreshSelectedItem)
    const token2 = PubSub.subscribe('localization.auto-translate-completed', refreshSelectedItem)

    return () => {
      PubSub.unsubscribe(token1)
      PubSub.unsubscribe(token2)
    }
  }, [selectedLocalizationItem, localizationController])

  // Get type styles (same as table chips)
  const getTypeStyles = (type) => {
    switch (type?.toLowerCase()) {
      case 'text':
        return "bg-admin_accent_3 text-white" // Green
      case 'object':
        return "bg-admin_accent_2 text-white" // Blue
      case 'image':
        return "bg-admin_accent_1 text-white" // Purple
      case 'video':
        return "bg-admin_accent_6 text-white" // Red
      case 'audio':
        return "bg-admin_accent_5 text-white" // Orange
      case 'file':
        return "bg-admin_accent_4 text-white" // Yellow
      default:
        return "bg-admin_neutral_3 text-white" // Grey
    }
  }

  // Close filter panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        // Check if the click target is part of a dropdown menu
        const isDropdownElement = event.target.closest('[role="menu"]') || 
                                 event.target.closest('.scrollbar-thin') ||
                                 event.target.type === 'checkbox' ||
                                 event.target.closest('label') ||
                                 event.target.closest('input[type="checkbox"]')
        
        if (!isDropdownElement) {
          // Use a small delay to avoid race conditions with dropdown selections
          setTimeout(() => {
            setIsFilterOpen(false)
          }, 100)
        }
      }
    }

    if (isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isFilterOpen])

  // Check if any filters are active
  const hasActiveFilters = pending || searchInLanguage.length > 0 || !selectedType.includes("Any")

  const headerControls = (
    <div className="flex flex-wrap items-center gap-2 flex-grow -mt-5">
      {languageContent?.typeStats && (
        <>
          {languageContent.typeStats.map(({ type, count }) => (
            <div
              key={type}
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getTypeStyles(type)}`}
            >
              <span className="uppercase tracking-wide">
                {type}: {count}
              </span>
            </div>
          ))}
          <div className="inline-flex items-center px-2 py-1 rounded-md text-xs font-bold bg-admin_paper text-admin_text border border-admin_primary_dk">
            <span className="uppercase tracking-wide">
              TOTAL: {languageContent.typeStats.reduce((sum, item) => sum + item.count, 0)}
            </span>
          </div>
        </>
      )}
    </div>
  )

  return (
    <div className="p-4 bg-admin_canvas min-h-screen">
      {/* Header */}
      <div className="w-full flex items-center justify-between mb-5">
        <Typography 
          component="h1"
          variant="admin_h3"
          className="ml-4"
        >
          Localization Keys
        </Typography>

        <div className="flex gap-2 items-center">
          <div className="relative" ref={filterRef}>
            {/* Filter Button */}
            <div className="relative">
              <Button
                variant="admin_secondary"
                className="w-10 p-0"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
              >
                <FunnelIcon className="w-4 h-4" />
              </Button>
              
              {/* Active Filter Indicator */}
              {hasActiveFilters && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-admin_error rounded-full flex items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-white rounded-full" />
                </div>
              )}
            </div>
            
            {isFilterOpen && (
              <div className="absolute min-w-[260px] top-full right-0 bg-admin_paper border border-admin_bordered rounded-lg shadow-xl z-50 p-4 mt-2">
                <div className="flex flex-col gap-4">
                  <CheckBox
                    variant="admin"
                    label={"Pending Only"}
                    isOn={pending}
                    onChange={setPending}
                  />

                  <div className="border-t border-admin_bordered">
                    <Typography 
                      variant="admin_txt1_dim" 
                      className="block my-3"
                    >
                      Search Mode
                    </Typography>

                    <div className="flex flex-col gap-2">
                      <CheckBox
                        variant="admin"
                        label={"Search by Key"}
                        isOn={searchField === "key"}
                        onChange={() => {
                          setSearchField(searchField === "key" ? "text" : "key")
                          setTableRefresh(!tableRefresh)
                        }}
                      />
                      <CheckBox
                        variant="admin"
                        label={"Search by Text"}
                        isOn={searchField === "text"}
                        onChange={() => {
                          setSearchField(searchField === "key" ? "text" : "key")
                          setTableRefresh(!tableRefresh)
                        }}
                      />
                    </div>
                  </div>

                  <div className="border-t border-admin_bordered">
                    <Typography 
                      variant="admin_txt1_dim" 
                      className="block mt-4 mb-1"
                    >
                      Language Filter
                    </Typography>

                    <DropDownSelect
                      variant="admin"
                      searchEnabled
                      options={languages}
                      fixedWidth="260px"
                      valueField={"code"}
                      placeholder={
                        localizationController?.languages?.length > 0
                          ? "Search in Language..."
                          : "No languages loaded"
                      }
                      value={searchInLanguage.map((code) =>
                        languages.find((item) => item.code === code)
                      ).filter(Boolean)}
                      onChange={(value) => {
                        setSearchInLanguage(value.map((item) => item.code))
                      }}
                      highlightOnSelection={true}
                      highlightResetValue={[]}
                    />
                  </div>

                  <div>
                    <Typography 
                      variant="admin_txt1_dim" 
                      className="block mb-1"
                    >
                      Type Filter
                    </Typography>

                    <DropDownSelect
                      variant="admin"
                      multiSelection
                      searchEnabled
                      options={typeOptions}
                      fixedWidth="260px"
                      maxHeight={"300px"}
                      valueField={"value"}
                      labelField={"label"}
                      placeholder="Filter by Type..."
                      value={selectedType.map((type) =>
                        typeOptions.find((option) => option.value === type)
                      )}
                      onChange={(value) => {
                        if (!value || value.length === 0) {
                          setSelectedType(["Any"])
                          return
                        }

                        // Check if "Any" was just selected
                        const anyJustSelected =
                          value.some((item) => item.value === "Any") &&
                          !selectedType.includes("Any")

                        // Check if a non-Any was just selected
                        const nonAnyJustSelected = value.some(
                          (item) =>
                            item.value !== "Any" && !selectedType.includes(item.value)
                        )

                        if (anyJustSelected) {
                          // If "Any" was selected, clear all other selections
                          setSelectedType(["Any"])
                        } else if (nonAnyJustSelected && selectedType.includes("Any")) {
                          // If another option was selected and "Any" was previously selected, remove "Any"
                          const newSelection = value
                            .filter((item) => item.value !== "Any")
                            .map((item) => item.value)
                          setSelectedType(newSelection)
                        } else {
                          // Normal case - just update the selection
                          setSelectedType(value.map((item) => item.value))
                        }
                      }}
                      highlightOnSelection={true}
                      highlightResetValue="Any"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <Button
            variant="admin_primary" 
            className="whitespace-nowrap" 
            onClick={() => setOpenAddKeyDialog(true)}
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />
            Add
          </Button>
        </div>
      </div>

      {/* Dialogs */}
      <LocalizationItemDialog
        controller={localizationController}
        isOpen={selectedLocalizationItem !== null}
        item={selectedLocalizationItem}
        onClose={() => setSelectedLocalizationItem(null)}
        onSave={(updatedItem) => {
          // Refresh the data table to show updated changes
          setTableRefresh(!tableRefresh)
          // Keep the dialog open - do not call setSelectedLocalizationItem(null)
        }}
      />

      <LocalizationKeyDialog
        controller={localizationController}
        isOpen={openAddKeyDialog}
        onClose={() => setOpenAddKeyDialog(false)}
      />

      <LocalizationRebuildDialog
        isOpen={openRebuildDialog}
        onClose={() => setOpenRebuildDialog(false)}
        controller={localizationController}
      />

      {/* Key data table */}
      <DataTable
        variant="admin_browser"
        apiMethod={ListLocalizationKeys}
        preferredHeight="75vh"
        itemHeight="h-[60px]"
        searchField={searchField}
        headerControls={headerControls}
        searchObject={searchObject}
        styleOverrides={{
          table: "w-full table-fixed",
          getRowStyle: (index, item) => {
            return "hover:bg-admin_neutral_1 transition-colors duration-200 cursor-pointer border-b border-admin_bordered/60"
          }
        }}
        requestFields={{
          language: "en",
          expand: true, // Include frozen arrays and other extended data
          ...(selectedType.length > 0 && !selectedType.includes("Any")
            ? {
                match: {
                  type:
                    selectedType.length === 1
                      ? selectedType[0].toLowerCase()
                      : selectedType.map((t) => t.toLowerCase()),
                },
              }
            : {}),
        }}
        sort={{ createdAt: -1 }}
        refreshKey={tableRefresh}
        renders={keyRenders}
        localUserPreferencesKey="ui.localizationKeys"
        noData={
          <Typography variant="admin_h3">
            No keys match the search criteria
          </Typography>
        }
      />
    </div>
  )
}